import React from 'react';
import { EstateAssets, EstateProfile } from '../../types';
import { hex } from '../designTokens';

interface MissingCategory {
    label: string;
    stepId: string | null;
}

interface CompletenessResult {
    percentage: number;
    qualitative: string;
    missing: MissingCategory[];
    confirmedCount: number;
    totalCount: number;
}

function computeCompleteness(
    assets: EstateAssets | undefined,
    profile: EstateProfile | undefined,
): CompletenessResult {
    const categories: { label: string; present: boolean; stepId: string | null }[] = [];

    categories.push({ label: 'Bank accounts', present: (assets?.bank_accounts ?? []).length > 0, stepId: 'banks' });
    categories.push({ label: 'Property details', present: (assets?.properties ?? []).length > 0, stepId: 'property' });
    categories.push({ label: 'Property valuations', present: (assets?.properties ?? []).every(p => p.estimated_value && p.estimated_value > 0) && (assets?.properties ?? []).length > 0, stepId: 'property' });
    categories.push({ label: 'Investments', present: (assets?.investments ?? []).length > 0 || assets?.completeness?.assets_confirmed === true, stepId: 'investments' });
    categories.push({ label: 'Pensions', present: (assets?.pensions ?? []).length > 0 || assets?.completeness?.assets_confirmed === true, stepId: 'pensions' });
    categories.push({ label: 'Debts & liabilities', present: (assets?.debts ?? []).length > 0 || assets?.completeness?.debts_confirmed === true, stepId: 'debts' });
    categories.push({ label: 'Lifetime gifts (7-year rule)', present: (assets?.lifetime_gifts ?? []).length > 0 || assets?.gifts_last_7_years?.exists === false, stepId: 'gifts' });
    categories.push({ label: 'Life insurance', present: assets?.completeness?.assets_confirmed === true, stepId: 'discovery' });
    categories.push({ label: 'Foreign assets', present: assets?.foreign_assets?.exists != null, stepId: 'complexity' });
    categories.push({ label: 'Business assets (BPR)', present: (assets?.bpr_assets ?? []).length > 0 || assets?.completeness?.assets_confirmed === true, stepId: 'bpr' });
    categories.push({ label: 'Date of death', present: !!profile?.deceased?.date_of_death, stepId: 'basics' });
    categories.push({ label: 'Marital status', present: !!profile?.deceased?.maritalStatus, stepId: 'basics' });
    categories.push({ label: 'Will status', present: !!profile?.representatives?.will_exists, stepId: 'beneficiaries' });

    const sp = profile?.iht_details?.predeceased_spouse;
    if (sp?.exists === true) {
        categories.push({ label: 'Spouse-transfer records', present: sp.records_available === 'full' || sp.records_available === 'partial', stepId: 'beneficiaries' });
    }

    const hasDescendantInfo = (assets?.properties ?? []).some(p => p.deceased_share_passes_to != null);
    categories.push({ label: 'Beneficiary details', present: hasDescendantInfo || !!profile?.representatives?.will_exists, stepId: 'beneficiaries' });

    const confirmedCount = categories.filter(c => c.present).length;
    const missing = categories.filter(c => !c.present).map(c => ({ label: c.label, stepId: c.stepId }));
    const percentage = Math.round((confirmedCount / categories.length) * 100);

    const qualitative = percentage >= 90 ? 'Complete'
        : percentage >= 75 ? 'Mostly complete'
            : percentage >= 50 ? 'Partially complete'
                : 'Early stage';

    return { percentage, qualitative, missing, confirmedCount, totalCount: categories.length };
}

interface Props {
    assets?: EstateAssets;
    profile?: EstateProfile;
    goToStep?: (stepId: string) => boolean;
}

export const EstateCompleteness: React.FC<Props> = ({ assets, profile, goToStep }) => {
    const { percentage, qualitative, missing } = computeCompleteness(assets, profile);

    return (
        <div
            className="rounded p-5"
            style={{ background: hex.surface, border: `1px solid ${hex.border}` }}
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-serif text-[18px] font-medium text-[#1A1A1A] m-0">Estate completeness</h3>
                <div className="flex items-baseline gap-2">
                    <span className="font-serif italic text-[15px] text-[#1A1A1A]">{qualitative}</span>
                    <span className="font-mono tabular-nums text-[12px] text-[#6B6762]">{percentage}%</span>
                </div>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-[#E8E4DB] rounded-full h-1.5 mb-4">
                <div
                    className="h-1.5 rounded-full bg-[#3730A3] transition-all"
                    style={{ width: `${percentage}%` }}
                />
            </div>

            {missing.length > 0 ? (
                <div>
                    <p className="text-[13px] text-[#6B6762] mb-2">Some areas may still need to be confirmed:</p>
                    <div className="flex flex-wrap gap-1.5">
                        {missing.slice(0, 6).map(item => (
                            item.stepId && goToStep ? (
                                <button
                                    key={item.label}
                                    onClick={() => goToStep(item.stepId!)}
                                    className="text-[12px] bg-transparent text-[#3730A3] hover:bg-[#EEF0FA] hover:border-[#C7CBEA] px-2.5 py-1 rounded border border-[#E8E4DB] cursor-pointer transition-colors"
                                >
                                    {item.label}
                                </button>
                            ) : (
                                <span key={item.label} className="text-[12px] text-[#3F3D39] px-2.5 py-1 rounded border border-[#E8E4DB]">
                                    {item.label}
                                </span>
                            )
                        ))}
                        {missing.length > 6 && (
                            <span className="text-[12px] text-[#AFA99F] italic px-2 py-1">
                                +{missing.length - 6} more
                            </span>
                        )}
                    </div>
                </div>
            ) : (
                <p className="font-serif italic text-[13px] text-[#1A1A1A]">
                    All key estate categories have been addressed.
                </p>
            )}

            <p className="text-[11px] text-[#AFA99F] italic mt-3">
                This is a workflow guide, not a legal completeness check.
            </p>
        </div>
    );
};
